import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Broadcast, Marker } from 'types/Asset';
import { getBroadcastDetailsURL, getCatchupPlayerURL, getLivePlayerURL } from 'utils/fnUrl';
import useYouboraContentTracking, { ContentTrackingParams } from 'hooks/useYouboraContentTracking';
import useYouboraSearchTracking from 'hooks/useYouboraSearchTracking';
import { StaticPageRoutes } from 'types/RouteTypes';
import { useSearch } from 'providers/useSearch/SearchContext';
import { LiveProgressIndicator } from 'components/LiveProgressIndicator/LiveProgressIndicator';
import { useAssetReplayCapable } from 'hooks/useAssetReplayCapable/useAssetReplayCapable';
import { useDetailLocationParams } from 'hooks/useDetailLocationParams';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { isFuture, isNow } from 'utils/fnDate';
import { useAuth } from 'providers/useAuth/AuthContext';
import { StandardCover } from 'types/Cover';
import { CardContext, ImageAspectRatio, ModuleType } from 'types/Common';
import { ProgressIndicator } from 'components/ProgressIndicator/ProgressIndicator';
import { MILLISECONDS } from 'utils/TimeUnit';
import { useBroadcastTimeCluster } from 'hooks/useBroadcastTimeCluster/useBroadcastTimeCluster';
import { CardTrackingData } from 'types/Tracking';
import { usePlayOptionHelper } from 'providers/usePlayOptions/PlayOptionsContext';
import { getBroadcastCardContentMarkerValue } from 'utils/fnContentMarker';
import { useChannels } from 'providers/useChannels/useChannels';
import { useApp } from 'providers/useApp/AppContext';
import { Card } from 'components/Cards/Card/Card';
import { CardVariant } from 'components/Cards/Card/cardVariants';

type AdapterPropTypes = {
    cover: StandardCover;
    cardContext: CardContext;
    type: ModuleType;
    trackingData: CardTrackingData;
    variant?: string;
    onMouseEnter?: (event: React.MouseEvent) => void;
    onMouseLeave?: (event: React.MouseEvent) => void;
    onClick?: (event: React.MouseEvent) => void;
};

const BroadcastCardAdapter: FC<AdapterPropTypes> = ({ cover, cardContext, type, trackingData, onMouseEnter, onMouseLeave, onClick }) => {
    const cardProps = {
        asset: cover,
        ...trackingData,
        onMouseEnter,
        onMouseLeave,
        onClick: (e: React.MouseEvent) => {
            if (onClick) {
                onClick(e);
            }
        },
    };

    if (cardContext === CardContext.COLLECTION) {
        return cover.landscape ? (
            <Card variant={CardVariant.LANDSCAPE} context={cardContext} {...cardProps} />
        ) : (
            <Card variant={CardVariant.PORTRAIT} context={cardContext} asset={cover} {...trackingData} />
        );
    }

    return cover.landscape && type !== ModuleType.VOD_MOVIE ? (
        <Card variant={CardVariant.LANDSCAPE} context={cardContext} asset={cover} {...trackingData} />
    ) : (
        <Card variant={CardVariant.PORTRAIT} context={cardContext} {...cardProps} />
    );
};

type PresenterPropTypes = {
    asset: Broadcast;
    type: ModuleType;
    cardContext: CardContext;
    onClick?: (event: React.MouseEvent<any>, data: Broadcast) => void;
    trackingData: CardTrackingData;
    variant?: string;
    aspectRatio?: ImageAspectRatio;
};

export const BroadcastCardPresenter: FC<PresenterPropTypes> = ({ asset, type, cardContext, onClick, trackingData }) => {
    const navigate = useNavigate();
    const { isGuest } = useAuth();
    const {
        id,
        program_id,
        title,
        image,
        image_url,
        startTime,
        endTime,
        isLive,
        bookmark,
        isChannelSubscribed,
        channelLogo,
        channelId,
        isPortrait,
        releaseYear,
        genres,
    } = asset;

    const { getChannelById } = useChannels();
    const resolvedChannelLogo = channelLogo ?? getChannelById(channelId)?.logo;

    const productionCountries = (asset as any).productionCountries as string[] | undefined;

    const metaData = [productionCountries?.length === 1 ? productionCountries[0] : '', releaseYear || '', genres?.length ? genres[0] : '']
        .filter(Boolean)
        .join(' ');
    const imageUrl = image || image_url;
    const coverId = (asset as any).kind === 'Movie' && program_id ? program_id : id;

    const { isReplayCapable } = useAssetReplayCapable(asset);
    const locationParamsPath = useDetailLocationParams();
    const { config } = useConfig();
    const { appLanguage } = useApp();

    const playOptionVerifier = usePlayOptionHelper();
    const timeCluster = useBroadcastTimeCluster(asset);

    const { searchTerm } = useSearch();
    const location = useLocation();
    const isSearchPage = location.pathname === StaticPageRoutes.SEARCH;

    const { handleContentFocus, handleContentFocusOut, handleContentClick, trackContentPlay } = useYouboraContentTracking();

    const { trackSearchClick } = useYouboraSearchTracking();

    const [contentMarker, setContentMarker] = useState<Marker>(null);

    const isInPlayer = () => location.pathname.includes('/play/live/');

    const onPlayClicked = (event) => {
        event.preventDefault();
        event.stopPropagation();

        trackContentPlay(asset.id);

        if (timeCluster === 'LIVE') {
            const playerUrl = getLivePlayerURL(asset.channelId, {
                ...(isInPlayer() && { live: true }),
            });
            navigate(playerUrl);
        } else {
            playOptionVerifier(asset, getCatchupPlayerURL(asset.id));
        }
    };

    useEffect(() => {
        if (timeCluster && asset && asset.startTime && asset.endTime) {
            setContentMarker(getBroadcastCardContentMarkerValue(isLive, timeCluster));
        } else {
            setContentMarker(null);
        }
    }, [timeCluster, asset]);

    const progress = (bookmark * 100) / MILLISECONDS.toSeconds(endTime - startTime);

    const getYouboraParams = (): ContentTrackingParams => ({
        section: trackingData['data-module-title'] || '',
        sectionOrder: trackingData['data-stripe-position'] || 1,
        column: trackingData['data-card-position'] || 1,
        row: 1,
        contentId: asset.id,
        contentLink: asset.id,
        contentLinkType: 'contentId',
    });

    const handleCardClick = () => {
        handleContentClick(getYouboraParams());

        if (isSearchPage && searchTerm) {
            const params = getYouboraParams();
            trackSearchClick({
                section: params.section,
                sectionOrder: params.sectionOrder,
                column: params.column,
                row: params.row,
                contentId: params.contentId,
                query: searchTerm,
                contentLink: params.contentLink,
                contentLinkType: params.contentLinkType,
            });
        }
    };

    const contentTrackingProps = {
        onMouseEnter: () => handleContentFocus(getYouboraParams()),
        onMouseLeave: handleContentFocusOut,
        onClick: handleCardClick,
    };

    return (
        <BroadcastCardAdapter
            cover={{
                uuid: id + startTime + endTime,
                id: coverId,
                path: getBroadcastDetailsURL(asset, locationParamsPath, appLanguage),
                title,
                image: imageUrl,
                landscapeImage: imageUrl,
                channelImage: resolvedChannelLogo,
                metaData,
                landscape: !isPortrait,
                hideDescription: true,
                circle: false,
                assetType: asset.type,
                contentMarker,
                rawData: asset,
                progressBar:
                    timeCluster === 'LIVE' ? (
                        <LiveProgressIndicator startTime={startTime} endTime={endTime} onLiveStateChanged={null} />
                    ) : (
                        !isFuture(startTime) && (
                            <ProgressIndicator
                                percentage={progress < config.app_config.player_settings.bookmark_threshold ? progress : 0}
                            />
                        )
                    ),
                renderPlayIcon: !isGuest && isChannelSubscribed && (isReplayCapable || isNow(startTime, endTime)),
                onPlayIconClicked: onClick ? null : onPlayClicked,
            }}
            cardContext={cardContext}
            type={type}
            trackingData={trackingData}
            {...contentTrackingProps}
        />
    );
};
